import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { DxButtonModule } from 'devextreme-angular/ui/button';
import { DxTooltipComponent, DxTooltipModule } from 'devextreme-angular/ui/tooltip';
import {
  DxTreeViewComponent,
  DxTreeViewModule,
  DxTreeViewTypes,
} from 'devextreme-angular/ui/tree-view';
import { PositionConfig } from 'devextreme/common/core/animation';
import { injectAppNavigation } from '../../app-navigation';

@Component({
  selector: 'app-side-navigation-menu',
  imports: [DxTreeViewModule, DxButtonModule, DxTooltipModule],
  templateUrl: './side-navigation-menu.html',
  styleUrl: './side-navigation-menu.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.compact]': 'compactMode()' },
})
export default class SideNavigationMenu {
  private readonly navigation = injectAppNavigation();
  private readonly menu = viewChild.required(DxTreeViewComponent);
  private readonly tooltip = viewChild(DxTooltipComponent);

  protected readonly tooltipText = signal('');
  protected readonly tooltipPosition: PositionConfig = {
    my: 'left',
    at: 'left',
    offset: { x: 64, y: 0 },
  };

  readonly selectedItemChanged = output<DxTreeViewTypes.ItemClickEvent>();
  readonly menuToggle = output<void>();

  readonly selectedItem = input<string>();
  readonly compactMode = input<boolean>(false);
  readonly showMenuToggle = input(false);

  protected readonly items = computed(() => {
    const compact = this.compactMode();
    type NavItem = (typeof this.navigation extends () => infer R ? R : never)[number];
    const prepare = (item: NavItem): NavItem & { expanded: boolean } => {
      const path = item.path && !item.path.startsWith('/') ? `/${item.path}` : item.path;
      const result: NavItem & { expanded: boolean } = { ...item, path, expanded: !compact };
      if (item.items) {
        result.items = item.items.map((child) => prepare(child));
      }
      return result;
    };
    return this.navigation().map(prepare);
  });

  constructor() {
    effect(() => {
      const selectedItem = this.selectedItem();
      const instance = this.menu().instance;
      if (instance) instance.selectItem(selectedItem);
    });

    effect(() => {
      const compactMode = this.compactMode();
      untracked(() => {
        const selectedItem = this.selectedItem();
        const instance = this.menu().instance;
        if (!instance) return;
        if (compactMode) this.menu().instance.collapseAll();
        else this.menu().instance.expandItem(selectedItem);
      });
    });
  }

  protected onItemClick(event: DxTreeViewTypes.ItemClickEvent) {
    this.selectedItemChanged.emit(event);
  }

  protected onMenuMouseOver(event: MouseEvent): void {
    this.showTooltipForTarget(event.target);
  }

  protected onMenuFocusIn(event: FocusEvent): void {
    this.showTooltipForTarget(event.target);
  }

  protected onMenuMouseLeave(): void {
    this.tooltip()?.instance.hide();
  }

  private showTooltipForTarget(target: EventTarget | null): void {
    if (!this.compactMode() || !(target instanceof HTMLElement)) return;
    const item = target.closest('.dx-treeview-item');
    if (!item) return;
    const label = item.querySelector('.nav-badge-item > span')?.textContent?.trim();
    const badge = item.querySelector('.nav-badge-item .dx-badge')?.textContent?.trim();
    let text = item.textContent?.trim() ?? '';
    if (label) {
      text = badge ? `${label} (${badge})` : label;
    }
    if (!text) return;
    this.tooltipText.set(text);
    this.tooltip()?.instance.show(item);
  }
}
